import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Alert,
  Box,
  Grid,
  TablePagination,
  TextField,
  InputAdornment,
  Stack,
  CircularProgress,
  Card,
  CardContent,
  CardActions,
  Chip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import Add from '@mui/icons-material/Add';
import GroupIcon from '@mui/icons-material/Group';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SchoolIcon from '@mui/icons-material/School';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { Link as RouterLink } from 'react-router-dom';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { format } from 'date-fns';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getOrganizationId } from '../utils/organizationContext';

const FoundationSchoolDashboard = () => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState([null, null]);
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState('');
  const [totalStats, setTotalStats] = useState({
    total: 0,
    pending: 0,
    completed: 0,
    active: 0
  });
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { organizationSlug: urlSlug } = useParams();
  const { user, organizationSlug } = useAuth();
  const [currentOrgId, setCurrentOrgId] = useState(null);

  // Load organization ID from user or URL slug
  useEffect(() => {
    const loadOrgId = async () => {
      if (user?.organization_id) {
        setCurrentOrgId(user.organization_id);
        return;
      }
      
      const slug = urlSlug || organizationSlug;
      if (slug && user?.role === 'system_admin' && !user?.organization_id) {
        try {
          const orgId = await getOrganizationId(slug);
          if (orgId) {
            setCurrentOrgId(orgId);
          }
        } catch (err) {
          console.error('Error fetching organization ID:', err);
        }
      }
    };
    loadOrgId();
  }, [urlSlug, organizationSlug, user]);

  const fetchStudents = useCallback(async () => {
    try {
      setLoading(true);
      
      const formatDate = (date) => {
        if (!date) return null;
        return format(date, 'yyyy-MM-dd');
      };

      const searchParams = new URLSearchParams({
        page: page + 1,
        limit: rowsPerPage,
        ...(searchQuery && { search: searchQuery }),
        ...(dateRange[0] && { startDate: formatDate(dateRange[0]) }),
        ...(dateRange[1] && { endDate: formatDate(dateRange[1]) })
      });

      if (currentOrgId && (user?.role === 'system_admin' || user?.organization_id === currentOrgId)) {
        searchParams.append('organization_id', currentOrgId);
      }

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/foundation-school/all?${searchParams}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      setStudents(response.data.data || []);
      setTotalCount(response.data.total || 0);
      setTotalStats({
        total: response.data.total || 0,
        pending: response.data.totalPending || 0,
        completed: response.data.totalCompleted || 0,
        active: response.data.totalActive || 0
      });
      setLoading(false);
      setError('');
    } catch (err) {
      console.error('Error fetching foundation school students:', err);
      const errorMessage = err.response?.data?.error || 'Failed to fetch foundation school students';
      setError(errorMessage);
      setStudents([]);
      setTotalCount(0);
      setLoading(false);
    }
  }, [page, rowsPerPage, searchQuery, dateRange, user, currentOrgId]);

  useEffect(() => {
    if (currentOrgId || user?.organization_id) {
      fetchStudents();
    }
  }, [fetchStudents, currentOrgId, user?.organization_id]);

  const handleDelete = async () => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/foundation-school/${selectedStudent.id}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      fetchStudents();
      setDeleteDialogOpen(false);
      setSelectedStudent(null);
    } catch (err) {
      setError('Failed to delete student');
    }
  };

  const handleViewDetails = (student) => {
    const slug = urlSlug || organizationSlug;
    const path = slug 
      ? `/${slug}/foundation-school/${student.id}`
      : `/foundation-school/${student.id}`;
    navigate(path);
  };

  const handleDownloadCertificate = async (student) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/foundation-school/${student.id}/certificate`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          responseType: 'blob'
        }
      );

      // Create a blob URL and trigger download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Foundation-School-Certificate-${student.first_name}-${student.last_name}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setError('');
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Failed to generate certificate';
      setError(errorMessage);
    }
  };



  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setPage(0);
  };

  const handleStartDateChange = (newValue) => {
    setDateRange([newValue, dateRange[1]]);
    setPage(0);
  };

  const handleEndDateChange = (newValue) => {
    setDateRange([dateRange[0], newValue]);
    setPage(0);
  };

  const handleClearFilters = () => {
    setDateRange([null, null]);
    setSearchQuery('');
    setPage(0);
  };

  const handleExport = async () => {
    try {
      setIsExporting(true);
      setExportError('');

      const formatDate = (date) => {
        if (!date) return null;
        return date.toISOString().split('T')[0];
      };

      const searchParams = new URLSearchParams({
        ...(searchQuery && { search: searchQuery }),
        ...(dateRange[0] && { startDate: formatDate(dateRange[0]) }),
        ...(dateRange[1] && { endDate: formatDate(dateRange[1]) })
      });

      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/foundation-school/export?${searchParams}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          },
          responseType: 'blob'
        }
      );

      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'wcito-foundation-school.csv';
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      } else {
        const now = new Date();
        const timestamp = now.toISOString().replace(/[:.]/g, '-').split('T')[0] + '-' + 
                         now.toTimeString().split(' ')[0].replace(/:/g, '-');
        filename = `wcito-foundation-school-${timestamp}.csv`;
      }
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Export error:', error);
      setExportError(error.response?.data?.error || 'Failed to export data');
    } finally {
      setIsExporting(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'active':
      case 'assigned':
        return 'primary';
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  return (
    <Container maxWidth="xl" sx={{ 
      mt: 4, 
      mb: 4,
      background: 'linear-gradient(135deg, #fef2f2 0%, #ffffff 100%)',
      minHeight: '100vh',
      py: 2
    }}>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom sx={{ 
            background: 'linear-gradient(135deg, #DC2626 0%, #991B1B 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontWeight: 'bold'
          }}>
            Foundation School Dashboard
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" gutterBottom>
            Manage and track foundation school enrollments
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={isExporting ? <CircularProgress size={20} /> : <DownloadIcon />}
            onClick={handleExport}
            disabled={isExporting}
            sx={{
              borderColor: '#DC2626',
              color: '#DC2626',
              '&:hover': {
                borderColor: '#B91C1C',
                backgroundColor: '#fef2f2',
              },
              px: 3,
              py: 1.5,
              borderRadius: 2,
            }}
          >
            {isExporting ? 'Exporting...' : 'Export CSV'}
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            component={RouterLink}
            to={organizationSlug ? `/${organizationSlug}/foundation-school` : "/foundation-school"}
            sx={{
              background: 'linear-gradient(135deg, #DC2626 0%, #991B1B 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #B91C1C 0%, #7F1D1D 100%)',
              },
              px: 3,
              py: 1.5,
              borderRadius: 2,
              boxShadow: '0 4px 20px rgba(220, 38, 38, 0.3)'
            }}
          >
            Add Student
          </Button>
        </Stack>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={3}>
          <Paper sx={{ 
            p: 2, 
            textAlign: 'center',
            background: 'linear-gradient(135deg, #DC2626 0%, #991B1B 100%)',
            color: 'white',
            boxShadow: '0 4px 20px rgba(220, 38, 38, 0.3)',
            transition: 'all 0.3s ease-in-out',
            cursor: 'pointer',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 6px 25px rgba(220, 38, 38, 0.4)'
            }
          }}>
            <GroupIcon sx={{ fontSize: 28, color: 'white', mb: 1, opacity: 0.9 }} />
            <Typography variant="h6" sx={{ color: 'white', mb: 1, fontSize: '0.9rem' }}>Total Students</Typography>
            <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>{totalStats.total}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Paper sx={{ 
            p: 2, 
            textAlign: 'center',
            background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
            color: 'white',
            boxShadow: '0 4px 20px rgba(245, 158, 11, 0.3)',
            transition: 'all 0.3s ease-in-out',
            cursor: 'pointer',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 6px 25px rgba(245, 158, 11, 0.4)'
            }
          }}>
            <WarningIcon sx={{ fontSize: 28, color: 'white', mb: 1, opacity: 0.9 }} />
            <Typography variant="h6" sx={{ color: 'white', mb: 1, fontSize: '0.9rem' }}>Pending</Typography>
            <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>{totalStats.pending}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Paper sx={{ 
            p: 2, 
            textAlign: 'center',
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            color: 'white',
            boxShadow: '0 4px 20px rgba(16, 185, 129, 0.3)',
            transition: 'all 0.3s ease-in-out',
            cursor: 'pointer',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 6px 25px rgba(16, 185, 129, 0.4)'
            }
          }}>
            <CheckCircleIcon sx={{ fontSize: 28, color: 'white', mb: 1, opacity: 0.9 }} />
            <Typography variant="h6" sx={{ color: 'white', mb: 1, fontSize: '0.9rem' }}>Completed</Typography>
            <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>{totalStats.completed}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Paper sx={{ 
            p: 2, 
            textAlign: 'center',
            background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
            color: 'white',
            boxShadow: '0 4px 20px rgba(59, 130, 246, 0.3)',
            transition: 'all 0.3s ease-in-out',
            cursor: 'pointer',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 6px 25px rgba(59, 130, 246, 0.4)'
            }
          }}>
            <SchoolIcon sx={{ fontSize: 28, color: 'white', mb: 1, opacity: 0.9 }} />
            <Typography variant="h6" sx={{ color: 'white', mb: 1, fontSize: '0.9rem' }}>Active</Typography>
            <Typography variant="h4" sx={{ color: 'white', fontWeight: 'bold' }}>{totalStats.active}</Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Filters and Search */}
      <Paper sx={{ 
        p: 3, 
        mb: 3,
        border: '1px solid #fecaca',
        background: 'linear-gradient(135deg, #fef2f2 0%, #fecaca 5%, #ffffff 100%)'
      }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {exportError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {exportError}
          </Alert>
        )}
        
        <Grid container spacing={2} alignItems="center" direction={isMobile ? "column" : "row"}>
          <Grid item xs={12} md={6}>
            <Stack direction={isMobile ? "column" : "row"} spacing={2} alignItems="center" width="100%">
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Start Date"
                  value={dateRange[0]}
                  onChange={handleStartDateChange}
                  slotProps={{
                    textField: {
                      size: "small",
                      fullWidth: isMobile
                    }
                  }}
                  sx={isMobile ? { width: '100%', mb: 1 } : {}}
                />
                <DatePicker
                  label="End Date"
                  value={dateRange[1]}
                  onChange={handleEndDateChange}
                  slotProps={{
                    textField: {
                      size: "small",
                      fullWidth: isMobile
                    }
                  }}
                  sx={isMobile ? { width: '100%', mb: 1 } : {}}
                />
              </LocalizationProvider>
            </Stack>
          </Grid>
          <Grid item xs={12} md={5}>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              placeholder="Search by name, phone, or email..."
              value={searchQuery}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} md={1}>
            <Button
              variant="outlined"
              onClick={handleClearFilters}
              fullWidth={isMobile}
              sx={{
                borderColor: '#DC2626',
                color: '#DC2626',
                '&:hover': {
                  borderColor: '#B91C1C',
                  backgroundColor: '#fef2f2',
                },
                py: 1.5,
                borderRadius: 2,
              }}
            >
              Clear
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {isMobile ? (
            <Box sx={{ mt: 2 }}>
              <Stack spacing={2}>
                {students.length === 0 ? (
                  <Alert severity="info">No students found</Alert>
                ) : (
                  students.map((student) => (
                    <Card key={student.id} sx={{ mb: 2 }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="h6">
                            {student.first_name} {student.last_name}
                          </Typography>
                          <Chip label={student.status} color={getStatusColor(student.status)} size="small" />
                        </Box>
                        
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          <strong>Enrollment Date:</strong> {format(new Date(student.enrollment_date), 'dd/MM/yyyy')}
                        </Typography>
                        
                        <Typography variant="body2" gutterBottom>
                          <strong>Phone:</strong> {student.phone_number || 'N/A'}
                        </Typography>
                        
                        <Typography variant="body2" gutterBottom>
                          <strong>Email:</strong> {student.email || 'N/A'}
                        </Typography>

                        {student.assigned_class && (
                          <Box sx={{ mt: 1 }}>
                            <Typography variant="body2" gutterBottom>
                              <strong>Assigned Class:</strong>{' '}
                              <Chip 
                                label={student.assigned_class === 'class_1' ? 'Class 1' : 'Class 2'} 
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            </Typography>
                            <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              <Typography variant="body2">
                                <strong>Completion Status:</strong>
                              </Typography>
                              {student.assigned_class === 'class_1' && (
                                <Chip
                                  label="Class 2 Pending"
                                  size="small"
                                  color="warning"
                                  variant="outlined"
                                />
                              )}
                              {student.assigned_class === 'class_2' && (
                                <Chip
                                  label="Completed"
                                  size="small"
                                  color="success"
                                  variant="filled"
                                />
                              )}
                            </Box>
                          </Box>
                        )}
                      </CardContent>
                      <CardActions sx={{ flexWrap: 'wrap', gap: 1 }}>
                        <Button
                          size="small"
                          startIcon={<VisibilityIcon />}
                          onClick={() => handleViewDetails(student)}
                          fullWidth
                        >
                          View Details
                        </Button>
                        {student.assigned_class === 'class_2' && (
                          <Button
                            size="small"
                            color="primary"
                            startIcon={<PictureAsPdfIcon />}
                            onClick={() => handleDownloadCertificate(student)}
                            fullWidth
                          >
                            Download Certificate
                          </Button>
                        )}
                        <Button
                          size="small"
                          color="error"
                          startIcon={<DeleteIcon />}
                          onClick={() => {
                            setSelectedStudent(student);
                            setDeleteDialogOpen(true);
                          }}
                          fullWidth
                        >
                          Delete
                        </Button>
                      </CardActions>
                    </Card>
                  ))
                )}
              </Stack>
              
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <TablePagination
                  component="div"
                  count={totalCount}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                />
              </Box>
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table aria-label="foundation school students table">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Enrollment Date</TableCell>
                      <TableCell>Phone</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Assigned Class</TableCell>
                      <TableCell>Completion Status</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {students.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center">
                          No students found
                        </TableCell>
                      </TableRow>
                    ) : (
                      students.map((student) => (
                        <TableRow key={student.id}>
                          <TableCell>
                            {student.first_name} {student.last_name}
                          </TableCell>
                          <TableCell>{format(new Date(student.enrollment_date), 'dd/MM/yyyy')}</TableCell>
                          <TableCell>{student.phone_number || 'N/A'}</TableCell>
                          <TableCell>{student.email || 'N/A'}</TableCell>
                          <TableCell>
                            {student.assigned_class ? (
                              <Chip 
                                label={student.assigned_class === 'class_1' ? 'Class 1' : 'Class 2'} 
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            ) : (
                              <Typography variant="body2" color="text.secondary">Not Assigned</Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              {student.assigned_class === 'class_1' && (
                                <Chip
                                  label="Class 2 Pending"
                                  size="small"
                                  color="warning"
                                  variant="outlined"
                                />
                              )}
                              {student.assigned_class === 'class_2' && (
                                <Chip
                                  label="Completed"
                                  size="small"
                                  color="success"
                                  variant="filled"
                                />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip label={student.status} color={getStatusColor(student.status)} size="small" />
                          </TableCell>
                          <TableCell align="center">
                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', flexWrap: 'wrap', alignItems: 'center' }}>
                              <IconButton
                                color="primary"
                                onClick={() => handleViewDetails(student)}
                                size="small"
                                title="View Details"
                              >
                                <VisibilityIcon />
                              </IconButton>
                              {student.assigned_class === 'class_2' && (
                                <IconButton
                                  color="primary"
                                  onClick={() => handleDownloadCertificate(student)}
                                  size="small"
                                  title="Download Certificate"
                                >
                                  <PictureAsPdfIcon />
                                </IconButton>
                              )}
                              <IconButton
                                color="error"
                                onClick={() => {
                                  setSelectedStudent(student);
                                  setDeleteDialogOpen(true);
                                }}
                                size="small"
                                title="Delete"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
              />
            </>
          )}
        </>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete {selectedStudent?.first_name} {selectedStudent?.last_name}?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error">Delete</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default FoundationSchoolDashboard;

